/**
 * Rebalance questionnaire page — loading/error shells, then intro and questions.
 *
 * Belongs here: route-level layout and wiring the flow to the shared question UI.
 * Does not belong here: cursor rules, submit, or question markup.
 */
import { useEffect } from 'react'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { QuestionCard } from '../../expert-review/shared/components/QuestionCard'
import { expertReviewSafeAreaTopStyle } from '../../expert-review/shared/expertReviewSafeArea'
import { StrategyQuestionnaireStageId } from '../../expert-review/shared/types/questionnaire.types'
import { DirectionalSlideSwap } from '../../expert-review/shared/transitions/DirectionalSlideSwap'
import { getStageIconSvg } from '../../expert-review/strategy-questionnaire/setup/stageAssets'
import {
  RebalanceQuestionnaireEventIds,
  RebalanceQuestionnaireScreen
} from '../analytics/rebalanceQuestionnaireEvents'
import { useRebalanceQuestionnaireContext } from '../RebalanceQuestionnaireProvider'
import { RebalanceTopBar } from '../ui/RebalanceTopBar'

const RISK_ASSESSMENT_STAGE_LABEL = 'Risk Assessment'

export default function RebalanceQuestionnairePage() {
  const flow = useRebalanceQuestionnaireContext()

  useEffect(() => {
    if (flow.isLoading || flow.isError || !flow.hasQuestionnaire) return
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: RebalanceQuestionnaireScreen.Questionnaire }
    })
  }, [flow.isLoading, flow.isError, flow.hasQuestionnaire])

  if (flow.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-5 py-16">
        <p className="text-gray-600">Loading…</p>
      </div>
    )
  }

  if (flow.isError) {
    return (
      <QuestionnaireMessage
        message="Failed to load questionnaire"
        testLocatorBack="rebalance-questionnaire-error-back"
        onBack={flow.handleIntroBack}
      />
    )
  }

  if (!flow.hasQuestionnaire) {
    return (
      <QuestionnaireMessage
        message="No questionnaire data"
        testLocatorBack="rebalance-questionnaire-empty-back"
        onBack={flow.handleIntroBack}
      />
    )
  }

  if (flow.showIntro) {
    return <QuestionnaireIntro onBack={flow.handleIntroBack} onStart={flow.dismissIntro} />
  }

  const contentBottomPadding = getBottomPadding() + 16
  const progressFill = Math.min(100, Math.max(0, flow.progressFill * 100))
  const currentQuestion = flow.currentQuestion

  return (
    <div
      className="flex h-screen min-h-0 flex-col bg-white"
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
    >
      <div style={expertReviewSafeAreaTopStyle()} className="flex shrink-0 flex-col gap-4 bg-white px-5 pb-3">
        <RebalanceTopBar
          onBack={flow.handleQuestionBack}
          onMenuExit={flow.handleIntroBack}
          analyticsScreen={RebalanceQuestionnaireScreen.Questionnaire}
          testLocatorBack="rebalance-questionnaire-back"
          testLocatorMenu="rebalance-questionnaire-menu"
        />
        <div className="flex w-full gap-1">
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-[24px] bg-[#F0F4F7]"
            role="progressbar"
            aria-valuenow={Math.round(progressFill)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Question progress"
          >
            <div
              className="h-full rounded-full bg-[#232323] transition-[width] duration-200 ease-out motion-reduce:transition-none"
              style={{ width: `${progressFill}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500" aria-hidden>
            <RenderHTML
              innerHTMLText={getStageIconSvg(StrategyQuestionnaireStageId.RiskAssessment, 'header')}
            />
          </span>
          <span className="text-sm font-medium text-gray-600">{RISK_ASSESSMENT_STAGE_LABEL}</span>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar">
        {currentQuestion ? (
          <div
            className="mx-auto flex max-w-2xl flex-1 flex-col overflow-hidden bg-white px-5 pt-6"
            style={{ paddingBottom: `${contentBottomPadding}px` }}
          >
            <DirectionalSlideSwap contentKey={currentQuestion.id} direction={flow.slideDirection}>
              <QuestionCard
                question={currentQuestion}
                answers={flow.answers}
                onAdvance={flow.handleAdvance}
                updateDraftAnswer={flow.updateDraftAnswer}
                onAmountClear={() => flow.clearAnswer(currentQuestion.id)}
                contextId={StrategyQuestionnaireStageId.RiskAssessment}
              />
            </DirectionalSlideSwap>
          </div>
        ) : (
          <p className="px-5 pt-6 text-gray-600">No questions to show.</p>
        )}
      </div>
    </div>
  )
}

function QuestionnaireMessage({
  message,
  testLocatorBack,
  onBack
}: {
  message: string
  testLocatorBack: string
  onBack: () => void
}) {
  return (
    <div className="flex h-screen flex-col bg-white px-5 py-6">
      <header className="shrink-0">
        <RebalanceTopBar
          onBack={onBack}
          onMenuExit={onBack}
          analyticsScreen={RebalanceQuestionnaireScreen.Questionnaire}
          testLocatorBack={testLocatorBack}
        />
      </header>
      <div className="flex flex-1 items-center justify-center">
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  )
}

function QuestionnaireIntro({ onBack, onStart }: { onBack: () => void; onStart: () => void }) {
  return (
    <div
      className="flex h-screen flex-col bg-white px-5"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 24px)`
      }}
    >
      <div className="shrink-0">
        <RebalanceTopBar
          onBack={onBack}
          analyticsScreen={RebalanceQuestionnaireScreen.Questionnaire}
          testLocatorBack="rebalance-questionnaire-intro-back"
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="h-[8px]" />
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden pb-1">
          <img
            src="/images/allocation/strategy-questionnaire-intro.png"
            alt=""
            className="max-h-full min-h-[300px] w-auto max-w-full object-contain"
            test-locator="rebalance-questionnaire-intro-image"
          />
        </div>
        <div className="shrink-0">
          <h1 className="text-[24px] font-[400] leading-[120%] text-[#232323] min-[400px]:text-[32px]">
            Find your risk profile
          </h1>
          <p className="pb-8 pt-4 text-[14px] leading-[140%] text-[#6A6E6E]">
            Answer a few questions to discover your risk profile and get fund recommendations tailored to you.
          </p>
          <PrimaryButton
            testLocator="rebalance-questionnaire-intro-start"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName: RebalanceQuestionnaireScreen.Questionnaire,
                  eventId: RebalanceQuestionnaireEventIds.IntroStart,
                  buttonLabel: 'Start assessment'
                }
              })
              onStart()
            }}
            label="Start assessment"
            primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
          />
        </div>
      </div>
    </div>
  )
}
